import useHelpTarget from '../hooks/useHelpTarget';

/**
 * BudgetExceededBanner
 *
 * Renders when an agent run finalises with ``status="budget_exceeded"``.
 * The per-turn cap (max LLM calls / wall seconds / input or output
 * tokens) is enforced by ``OperationRecorder.check_budget()`` and is a
 * "clean exit" outcome — the run is not broken, the question is just
 * larger than the per-turn ceiling permits.
 *
 * Banner copy aims at the action the user can take next: narrow the
 * question or split it up, then retry.
 *
 * Props:
 *   limit    — which cap fired: ``llm_calls`` | ``wall_seconds`` |
 *              ``input_tokens`` | ``output_tokens``
 *   observed — measured value at the moment the cap tripped
 *   cap      — the ceiling value
 *   onDismiss — close-button handler
 */
const LIMIT_LABELS = {
  llm_calls: 'LLM call cap',
  wall_seconds: 'Wall-clock cap',
  input_tokens: 'Input-token cap',
  output_tokens: 'Output-token cap',
};

const LIMIT_UNITS = {
  llm_calls: 'calls',
  wall_seconds: 'seconds',
  input_tokens: 'tokens',
  output_tokens: 'tokens',
};

const LIMIT_HINTS = {
  llm_calls:
    'The agent made many tool calls. Try narrowing the question — fewer documents, a single topic, or a tighter date range.',
  wall_seconds:
    'The run hit its wall-clock cap. Re-run with a more focused question, or split it into smaller follow-ups.',
  input_tokens:
    'The agent read too much context. Scope to specific documents or folders before asking.',
  output_tokens:
    'The agent tried to produce a very long answer. Ask for a shorter form (a list, a summary, a single section).',
};

function formatValue(limit, value) {
  if (value == null) return '';
  if (limit === 'wall_seconds') {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return n >= 60 ? `${(n / 60).toFixed(1)}m` : `${Math.round(n)}s`;
  }
  // Token + call counts: integer with thousands separator
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString();
}

export default function BudgetExceededBanner({ limit, observed, cap, onDismiss }) {
  const helpRef = useHelpTarget({
    slug: 'cost-limits-and-budget',
    label: 'Budget exceeded banner',
    question: 'Why did this turn hit a budget cap and what should I do next?',
  });
  const label = LIMIT_LABELS[limit] || 'Budget cap';
  const unit = LIMIT_UNITS[limit] || '';
  const hint = LIMIT_HINTS[limit] || 'Try a more focused question.';
  const observedStr = formatValue(limit, observed);
  const capStr = formatValue(limit, cap);

  // Render the measurements only when we have them — older runs (or
  // a future cap type we haven't labelled yet) still get a useful
  // banner even without numbers.
  const measurement =
    observedStr && capStr
      ? ` (${observedStr} / ${capStr}${unit ? ' ' + unit : ''})`
      : '';

  return (
    <div className="alert alert-warning" role="status" aria-live="polite" ref={helpRef}>
      <strong>{label} reached{measurement}.</strong>
      <span style={{ marginLeft: 6 }}>{hint}</span>
      {onDismiss && (
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={onDismiss}
          style={{ marginLeft: 12 }}
          aria-label="Dismiss budget banner"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}
